import { Controller } from "@hotwired/stimulus"

// A drawing board for the seating plan.
//
// Canvas-like without a <canvas>: the plan is a box the size of the hall, the
// background image fills it, and every block is an absolutely positioned div on
// top. That keeps blocks as real elements — selectable, focusable, styleable —
// and means the same pixel grid the public plan uses is the one being drawn on.
//
// Panning and zooming are a transform on the board; nothing about the stored
// coordinates changes with them.
//
// Nothing is written to the database until Save is pressed. Moving blocks around
// is thinking out loud, and a plan halfway through being rearranged is not one
// anybody should be looking at — so the working copy lives in localStorage,
// survives a reload, and goes to the server in one go.
export default class extends Controller {
    static targets = ["board", "plan", "viewport", "zoom", "list", "inspector", "empty",
                      "status", "drawing", "scale", "rotation"]
    static values = {
        url: String,          // .../seating_plans/:id/blocks
        planUrl: String,      // .../seating_plans/:id
        width: Number,
        height: Number,
        cell: { type: Number, default: 16 },
        margin: { type: Number, default: 32 },
        planId: String,
    }

    connect() {
        this.scale = 1
        this.pan = { x: 0, y: 0 }
        this.selected = null
        this.drag = null
        this.dirty = false

        this.restore()

        this.onPointerMove = this.onPointerMove.bind(this)
        this.onPointerUp = this.onPointerUp.bind(this)
        window.addEventListener("pointermove", this.onPointerMove)
        window.addEventListener("pointerup", this.onPointerUp)

        this.render()
        this.syncSave()
        this.sizeDrawing()
        requestAnimationFrame(() => this.fit())
    }

    // The drawing layer is exactly the image: only then is its centre — the
    // point rotation turns about — the centre of what you can see.
    sizeDrawing() {
        if (!this.hasDrawingTarget) return

        const url = this.drawingTarget.style.backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1]
        if (!url) return

        const image = new Image()
        image.onload = () => {
            if (!image.naturalWidth) return

            this.imageRatio = image.naturalHeight / image.naturalWidth
            this.drawingTarget.style.height = `${Math.round(this.widthValue * this.imageRatio)}px`

            // Nobody has fitted it yet, so do it for them.
            if (this.untouched) this.autoFit()
        }
        image.src = url
    }

    get untouched() {
        return Number(this.scaleTarget?.value) === 1 && Number(this.rotationTarget?.value) === 0
    }

    // Turns and scales the drawing to fill the hall, picking whichever of
    // upright or on-its-side leaves less of the box empty.
    //
    // A floorplan exported landscape for a hall that is drawn tall is the usual
    // case, and squinting at two sliders to work out "90 and about 0.6" is not a
    // job for a person.
    autoFit() {
        if (!this.hasDrawingTarget || !this.imageRatio) return

        const planW = this.widthValue
        const planH = this.heightValue
        // The layer is planW wide and planW * ratio tall before any transform.
        const drawnH = planW * this.imageRatio

        const options = [
            { angle: 0, scale: Math.min(planW / planW, planH / drawnH) },
            { angle: 90, scale: Math.min(planW / drawnH, planH / planW) },
        ]
        const best = options.reduce((a, b) => (b.scale > a.scale ? b : a))

        this.scaleTarget.value = Math.min(3, Math.max(0.2, best.scale)).toFixed(2)
        this.rotationTarget.value = String(best.angle)
        this.transformBackground()
    }

    // --- the draft ----------------------------------------------------------

    get storageKey() {
        return `plan-editor:${this.planIdValue}`
    }

    // Put back whatever was being worked on before the page went away.
    restore() {
        let draft
        try {
            draft = JSON.parse(localStorage.getItem(this.storageKey) || "null")
        } catch {
            draft = null
        }
        if (!draft?.blocks) return

        let applied = 0
        this.blocks.forEach((block) => {
            const saved = draft.blocks[block.dataset.blockId]
            if (!saved) return

            Object.assign(block.dataset, saved)
            applied += 1
        })

        if (draft.background) {
            if (this.hasScaleTarget && draft.background.scale) {
                this.scaleTarget.value = draft.background.scale
            }
            if (this.hasRotationTarget && draft.background.rotation) {
                this.rotationTarget.value = draft.background.rotation
            }
            applied += 1
        }

        if (applied) {
            this.transformBackground()
            this.markDirty()
        }
    }

    markDirty() {
        this.dirty = true
        this.element.classList.add("is-dirty")
        this.syncSave()

        const blocks = {}
        this.blocks.forEach((block) => {
            blocks[block.dataset.blockId] = {
                x: block.dataset.x, y: block.dataset.y,
                rows: block.dataset.rows, columns: block.dataset.columns,
                title: block.dataset.title, titlePosition: block.dataset.titlePosition,
                invertY: block.dataset.invertY,
            }
        })

        const background = {
            scale: this.hasScaleTarget ? this.scaleTarget.value : null,
            rotation: this.hasRotationTarget ? this.rotationTarget.value : null,
        }

        try {
            localStorage.setItem(this.storageKey, JSON.stringify({ blocks, background, at: Date.now() }))
        } catch {
            // Out of quota or a private window: the change still stands for this
            // page, it just will not survive a reload.
        }
    }

    markClean() {
        this.dirty = false
        this.element.classList.remove("is-dirty")
        this.syncSave()
        try {
            localStorage.removeItem(this.storageKey)
        } catch {
            // Nothing to clear.
        }
    }

    disconnect() {
        window.removeEventListener("pointermove", this.onPointerMove)
        window.removeEventListener("pointerup", this.onPointerUp)
    }

    // --- the view -----------------------------------------------------------

    render() {
        this.planTarget.style.width = `${this.widthValue}px`
        this.planTarget.style.height = `${this.heightValue}px`

        this.pan = this.clampPan(
            this.widthValue * this.scale, this.heightValue * this.scale,
            this.viewportTarget.clientWidth, this.viewportTarget.clientHeight
        )
        this.boardTarget.style.transform =
            `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`
        if (this.hasZoomTarget) this.zoomTarget.textContent = `${Math.round(this.scale * 100)}%`

        this.blocks.forEach((block) => this.place(block))
        this.syncList()
        this.syncEmpty()
    }

// Panning stops at the hall's edges plus a little air, so the plan cannot be
    // dragged off into the void and lost. The size is the one the backend drew
    // the box at — see SeatingPlan#grid_width.
    clampPan(contentWidth, contentHeight, viewportWidth, viewportHeight) {
        const margin = this.marginValue

        const axis = (value, viewport, content) => {
            // Smaller than the window: nothing to pan, so it sits centred.
            if (content + margin * 2 <= viewport) return (viewport - content) / 2

            return Math.min(margin, Math.max(viewport - content - margin, value))
        }

        return {
            x: axis(this.pan.x, viewportWidth, contentWidth),
            y: axis(this.pan.y, viewportHeight, contentHeight),
        }
    }

    get blocks() {
        return Array.from(this.planTarget.querySelectorAll("[data-block-id]"))
    }

    place(block) {
        const { x, y, rows, columns } = this.read(block)
        block.style.left = `${x}px`
        block.style.top = `${y}px`
        block.style.width = `${columns * this.cellValue}px`
        block.style.height = `${rows * this.cellValue}px`
        block.classList.toggle("is-selected", block === this.selected)

        const seats = block.querySelector(".plan-block__seats")
        if (seats) {
            seats.style.gridTemplateColumns = `repeat(${columns}, 1fr)`
            const wanted = rows * columns

            // Grow: the extra cells are seats that do not exist yet.
            while (seats.children.length < wanted) {
                const seat = document.createElement("span")
                seat.className = "plan-block__seat is-new"
                seat.textContent = String(seats.children.length + 1)
                seats.append(seat)
            }

            // Shrink: the ones past the grid are dropped — but a dropped seat
            // somebody holds a ticket for is a conflict, not a change.
            let losing = 0
            Array.from(seats.children).forEach((seat, index) => {
                const dropped = index >= wanted
                seat.classList.toggle("is-dropped", dropped)
                if (dropped && seat.dataset.taken === "true") losing += 1
            })

            // Placeholders never existed, so shrinking past them costs nothing.
            while (seats.children.length > wanted &&
                   seats.lastElementChild.classList.contains("is-new")) {
                seats.lastElementChild.remove()
            }

            block.classList.toggle("is-conflict", losing > 0)
            block.dataset.losing = String(losing)
        }

        const size = block.querySelector(".plan-block__size")
        if (size) {
            const losing = Number(block.dataset.losing || 0)
            size.textContent = losing
                ? `${rows}×${columns} · ${losing} belegt!`
                : `${rows}×${columns}`
        }
    }

    // Any block that would cost somebody their seat blocks the save.
    get conflicts() {
        return this.blocks.filter((block) => Number(block.dataset.losing || 0) > 0)
    }

    syncSave() {
        const blocked = this.conflicts.length > 0
        this.element.classList.toggle("has-conflict", blocked)

        const save = this.element.querySelector(".plan-editor__save")
        if (save) save.disabled = blocked

        if (this.hasStatusTarget) {
            this.statusTarget.hidden = !this.dirty && !blocked
            this.statusTarget.classList.toggle("is-blocked", blocked)
            this.statusTarget.querySelector("[data-label]").textContent = blocked
                ? `${this.conflicts.length} Block(e) würden belegte Plätze verlieren`
                : "nicht gespeichert"
        }
    }

    // --- the drawing --------------------------------------------------------

    transformBackground() {
        if (!this.hasDrawingTarget) return

        const scale = this.hasScaleTarget ? Number(this.scaleTarget.value) : 1
        const angle = this.hasRotationTarget ? Number(this.rotationTarget.value) : 0
        this.drawingTarget.style.transform = `rotate(${angle}deg) scale(${scale})`
        this.markDirty()
    }

    read(block) {
        return {
            id: Number(block.dataset.blockId),
            x: Number(block.dataset.x),
            y: Number(block.dataset.y),
            rows: Number(block.dataset.rows),
            columns: Number(block.dataset.columns),
            title: block.dataset.title || "",
        }
    }

    // --- selection ----------------------------------------------------------

    select(event) {
        const block = event.currentTarget.closest("[data-block-id]")
        this.selected = block
        this.render()
        this.fillInspector()
    }

    selectFromList(event) {
        const id = event.currentTarget.dataset.blockId
        this.selected = this.blocks.find((block) => block.dataset.blockId === id)
        this.render()
        this.fillInspector()
    }

    clear(event) {
        // Only a click on the empty board, not one that bubbled up from a block.
        if (event.target !== this.planTarget && event.target !== this.viewportTarget) return
        this.selected = null
        this.render()
        this.fillInspector()
    }

    fillInspector() {
        if (!this.hasInspectorTarget) return

        this.inspectorTarget.hidden = !this.selected
        if (!this.selected) return

        const data = this.read(this.selected)
        this.field("title").value = data.title
        this.field("rows").value = data.rows
        this.field("columns").value = data.columns
        this.field("position_x").value = data.x
        this.field("position_y").value = data.y

        const side = this.field("title_position")
        if (side) side.value = this.selected.dataset.titlePosition || "left"
        const invert = this.field("invert_y")
        if (invert) invert.checked = this.selected.dataset.invertY === "true"
    }

    field(name) {
        return this.inspectorTarget.querySelector(`[data-field="${name}"]`)
    }

    // Typing in the inspector moves the block on the board straight away; the
    // save is what waits for the change event.
    edit() {
        if (!this.selected) return

        this.selected.dataset.rows = Math.max(1, Number(this.field("rows").value) || 1)
        this.selected.dataset.columns = Math.max(1, Number(this.field("columns").value) || 1)
        this.selected.dataset.x = Number(this.field("position_x").value) || 0
        this.selected.dataset.y = Number(this.field("position_y").value) || 0
        this.selected.dataset.title = this.field("title").value

        const label = this.selected.querySelector(".plan-block__title")
        if (label) label.textContent = this.field("title").value

        this.render()
        this.markDirty()
    }

    commit() {
        if (!this.selected) return

        this.edit()
        const side = this.field("title_position")
        const invert = this.field("invert_y")
        if (side) this.selected.dataset.titlePosition = side.value
        if (invert) this.selected.dataset.invertY = String(invert.checked)

        this.markDirty()
    }

    // --- dragging -----------------------------------------------------------

    startBlockDrag(event) {
        if (event.button !== 0) return

        const block = event.currentTarget.closest("[data-block-id]")
        this.selected = block
        this.fillInspector()

        const mode = event.currentTarget.dataset.handle ? "resize" : "move"
        const data = this.read(block)
        this.drag = {
            mode, block,
            startX: event.clientX, startY: event.clientY,
            originX: data.x, originY: data.y,
            originRows: data.rows, originColumns: data.columns,
        }
        this.render()
        event.preventDefault()
    }

    startPan(event) {
        // Middle button, or a left drag on the board itself rather than a block.
        if (event.button !== 1 && !(event.button === 0 && event.target === this.planTarget)) return

        this.drag = { mode: "pan", startX: event.clientX, startY: event.clientY, ...this.pan }
        this.element.classList.add("is-panning")
        event.preventDefault()
    }

    onPointerMove(event) {
        if (!this.drag) return

        const dx = (event.clientX - this.drag.startX)
        const dy = (event.clientY - this.drag.startY)

        if (this.drag.mode === "pan") {
            this.pan = { x: this.drag.x + dx, y: this.drag.y + dy }
            this.render()
            return
        }

        // Board pixels, not screen pixels — a drag at 50% zoom moves half as far.
        const planX = dx / this.scale
        const planY = dy / this.scale
        const block = this.drag.block

        if (this.drag.mode === "move") {
            // Snapped to the seat grid, so two blocks side by side line up.
            block.dataset.x = Math.max(0, this.snap(this.drag.originX + planX))
            block.dataset.y = Math.max(0, this.snap(this.drag.originY + planY))
        } else {
            block.dataset.columns = Math.max(1, this.drag.originColumns + Math.round(planX / this.cellValue))
            block.dataset.rows = Math.max(1, this.drag.originRows + Math.round(planY / this.cellValue))
        }

        this.place(block)
        this.fillInspector()
    }

    snap(value) {
        return Math.round(value / this.cellValue) * this.cellValue
    }

    onPointerUp() {
        if (!this.drag) return

        const { mode } = this.drag
        this.drag = null
        this.element.classList.remove("is-panning")

        if (mode === "pan") return
        this.markDirty()
        this.syncList()
    }

    // --- zoom and pan -------------------------------------------------------

    zoomIn() { this.setScale(this.scale * 1.25) }
    zoomOut() { this.setScale(this.scale / 1.25) }

    setScale(value) {
        this.scale = Math.min(4, Math.max(0.1, value))
        this.render()
    }

    // Zooming around a point: the spot under the cursor stays put, which is what
    // makes a wheel feel like a magnifier rather than a scrollbar.
    zoomAt(factor, clientX, clientY) {
        const box = this.viewportTarget.getBoundingClientRect()
        const x = clientX - box.left
        const y = clientY - box.top

        const next = Math.min(4, Math.max(0.1, this.scale * factor))
        const ratio = next / this.scale

        this.pan = { x: x - (x - this.pan.x) * ratio, y: y - (y - this.pan.y) * ratio }
        this.scale = next
        this.render()
    }

    // A trackpad pinch arrives as a wheel with ctrlKey; a plain wheel scrolls the
    // board, which is what a wheel does everywhere else.
    onWheel(event) {
        event.preventDefault()

        if (event.ctrlKey || event.metaKey) {
            this.zoomAt(Math.exp(-event.deltaY / 200), event.clientX, event.clientY)
            return
        }

        this.pan = { x: this.pan.x - event.deltaX, y: this.pan.y - event.deltaY }
        this.render()
    }

    // Whole plan, centred — the "I am lost" button.
    fit() {
        const box = this.viewportTarget.getBoundingClientRect()
        if (!box.width) return

        this.scale = Math.min(1, (box.width - 32) / this.widthValue, (box.height - 32) / this.heightValue)
        this.pan = {
            x: (box.width - this.widthValue * this.scale) / 2,
            y: 16,
        }
        this.render()
    }

    // --- saving -------------------------------------------------------------

    // The manager routes carry ?locale=de, so an id cannot just be stuck on the
    // end — that produced "/blocks?locale=de/100" and a routing error.
    blockUrl(id) {
        const url = new URL(this.urlValue, window.location.origin)
        url.pathname = `${url.pathname.replace(/\/$/, "")}/${id}`
        return `${url.pathname}${url.search}`
    }

    async add() {
        const created = await this.request(this.urlValue, "POST", {})
        if (!created) return

        // Inserted rather than reloaded, so an unsaved draft survives.
        this.planTarget.insertAdjacentHTML("beforeend", this.markup(created))
        this.selected = this.planTarget.lastElementChild
        this.render()
        this.fillInspector()
    }

    markup(block) {
        return `
            <div class="plan-block" data-block-id="${block.id}"
                 data-x="${block.position_x}" data-y="${block.position_y}"
                 data-rows="${block.rows}" data-columns="${block.columns}"
                 data-title="${block.title}" data-title-position="${block.title_position || "left"}"
                 data-invert-y="${block.invert_y}"
                 data-action="pointerdown->plan-editor#startBlockDrag click->plan-editor#select">
                <span class="plan-block__title">${block.title}</span>
                <div class="plan-block__seats"></div>
                <span class="plan-block__size"></span>
                <span class="plan-block__handle" data-handle="resize"
                      data-action="pointerdown->plan-editor#startBlockDrag"></span>
            </div>`
    }

    async remove() {
        if (!this.selected) return

        const { id, title } = this.read(this.selected)
        if (!window.confirm(`Block ${title} löschen?`)) return

        const response = await this.request(this.blockUrl(id), "DELETE")
        if (response === null) return

        this.selected.remove()
        this.selected = null
        this.render()
        this.fillInspector()
    }

    // The Save button: the whole board, in one go.
    async save() {
        if (this.conflicts.length) return

        if (this.hasDrawingTarget) {
            const saved = await this.request(this.planUrlValue, "PATCH", {
                seating_plan: {
                    background_scale: this.scaleTarget?.value,
                    background_rotation: this.rotationTarget?.value,
                },
            })
            if (!saved) return
        }

        for (const block of this.blocks) {
            const data = this.read(block)
            const saved = await this.request(this.blockUrl(data.id), "PATCH", {
                seating_plan_block: {
                    title: data.title,
                    title_position: block.dataset.titlePosition,
                    invert_y: block.dataset.invertY,
                    position_x: data.x,
                    position_y: data.y,
                    rows: data.rows,
                    columns: data.columns,
                },
            })
            if (!saved) return
        }

        this.markClean()
        this.syncList()
    }

    // Throw the draft away and go back to what is stored.
    discard() {
        if (this.dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return

        this.markClean()
        window.location.reload()
    }

    async request(url, method, body) {
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || "",
                },
                body: body ? JSON.stringify(body) : undefined,
            })

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}))
                window.alert((payload.errors || ["Konnte nicht gespeichert werden."]).join("\n"))
                return null
            }

            return response.status === 204 ? {} : await response.json()
        } catch {
            window.alert("Konnte nicht gespeichert werden.")
            return null
        }
    }

    // --- the side list ------------------------------------------------------

    syncList() {
        if (!this.hasListTarget) return

        this.listTarget.replaceChildren(...this.blocks
            .sort((a, b) => (a.dataset.title || "").localeCompare(b.dataset.title || ""))
            .map((block) => this.row(block)))
    }

    row(block) {
        const { id, rows, columns, title } = this.read(block)
        const item = document.createElement("button")
        item.type = "button"
        item.className = "plan-list__row"
        item.classList.toggle("is-selected", block === this.selected)
        item.dataset.blockId = String(id)
        item.dataset.action = "plan-editor#selectFromList"
        item.innerHTML =
            `<span class="plan-list__title"></span>` +
            `<span class="plan-list__meta">${rows}×${columns} · ${rows * columns}</span>`
        item.querySelector(".plan-list__title").textContent = title || "—"
        return item
    }

    syncEmpty() {
        if (this.hasEmptyTarget) this.emptyTarget.hidden = this.blocks.length > 0
    }
}
